import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useAuth } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { useFulfillmentGeneralSettings } from '@/hooks/use-fulfillment-settings';
import { buildSidebarMenuItems, type SidebarMenuItems } from '@/components/Layout/Sidebar/menuItems';
import { accountingVoucherSourceRoute, purchasingReceivingSourceRoute } from '@/constants/routes';
import { userRolesOf } from '@/utils/userRoles';

type MenuItem = NonNullable<MenuProps['items']>[number];

export interface ActiveMenuChild {
  key: string;
  path: string;
  label: React.ReactNode;
}

export interface ActiveMenuGroup {
  key: string;
  label: React.ReactNode;
  children: ActiveMenuChild[];
  activeChild: ActiveMenuChild | null;
}

interface FlatChild {
  key: string;
  path: string;
  label: React.ReactNode;
  rootKey: string;
  parentKey: string;
  parentLabel: React.ReactNode;
  siblings: ActiveMenuChild[];
}

function normalizeKey(key: string): string {
  const withoutScopedSuffix = key.split('__')[0];
  return withoutScopedSuffix.endsWith('-root')
    ? withoutScopedSuffix.slice(0, -'-root'.length)
    : withoutScopedSuffix;
}

function hasChildren(
  item: MenuItem,
): item is MenuItem & { children: NonNullable<SidebarMenuItems> } {
  return !!item && 'children' in item && Array.isArray((item as { children?: unknown }).children);
}

function flattenChildren(items: SidebarMenuItems): FlatChild[] {
  const flat: FlatChild[] = [];

  const walk = (nodes: SidebarMenuItems, rootKey?: string) => {
    (nodes ?? []).forEach((item) => {
      if (!item || !('key' in item)) return;
      if ((item as { type?: string }).type === 'divider') return;
      if (!hasChildren(item)) return;

      const parentKey = String(item.key);
      const currentRootKey = rootKey ?? parentKey;
      const parentLabel = (item as { label?: React.ReactNode }).label ?? parentKey;
      const childItems = item.children
        .filter((c): c is MenuItem & { key: React.Key } => !!c && 'key' in c)
        .filter((c) => (c as { type?: string }).type !== 'divider');

      const siblings: ActiveMenuChild[] = childItems
        .filter((child) => !hasChildren(child))
        .map((child) => ({
          key: String(child.key),
          path: normalizeKey(String(child.key)),
          label: (child as { label?: React.ReactNode }).label ?? String(child.key),
        }));

      siblings.forEach((sibling) => {
        flat.push({
          key: sibling.key,
          path: sibling.path,
          label: sibling.label,
          rootKey: currentRootKey,
          parentKey,
          parentLabel,
          siblings,
        });
      });

      walk(childItems, currentRootKey);
    });
  };

  walk(items);

  return flat;
}

function findByPath(flat: FlatChild[], pathname: string): FlatChild | null {
  let best: FlatChild | null = null;
  let bestLen = -1;
  let bestHasAcct = false;

  for (const entry of flat) {
    if (entry.path === pathname) {
      const hasAcct = entry.key.includes('__acct');
      if (!best || entry.path.length > bestLen || (entry.path.length === bestLen && hasAcct && !bestHasAcct)) {
        best = entry;
        bestLen = entry.path.length;
        bestHasAcct = hasAcct;
      }
      continue;
    }
    if (entry.path !== '/' && pathname.startsWith(entry.path + '/')) {
      if (entry.path.length > bestLen) {
        best = entry;
        bestLen = entry.path.length;
        bestHasAcct = entry.key.includes('__acct');
      } else if (entry.path.length === bestLen) {
        const hasAcct = entry.key.includes('__acct');
        if (hasAcct && !bestHasAcct) {
          best = entry;
          bestHasAcct = hasAcct;
        }
      }
    }
  }

  return best;
}

export function useActiveMenuGroup(): ActiveMenuGroup | null {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useRbac();
  const { user } = useAuth();
  const { data: fulfillmentGeneralSettings } = useFulfillmentGeneralSettings();
  const bulkPackingEnabled = fulfillmentGeneralSettings?.data.bulk_packing_enabled === true;

  const flat = useMemo(
    () =>
      flattenChildren(
        buildSidebarMenuItems({
          hasPermission,
          onNavigate: navigate,
          userRole: user?.role,
          userRoles: userRolesOf(user),
          bulkPackingEnabled,
        }),
      ),
    [bulkPackingEnabled, hasPermission, navigate, user?.role, user?.roles],
  );

  return useMemo(() => {
    const match = findByPath(flat, location.pathname);
    if (!match) return null;
    let activeChild = match.siblings.find((s) => s.key === match.key) ?? null;
    // TCR-685 — a voucher-create screen opened from the Tiền mặt / Tiền gửi tab keeps that
    // tab active in the top strip, instead of falling back to "Tất cả thu chi".
    const sourceRoute = accountingVoucherSourceRoute(location.pathname, location.search)
      ?? purchasingReceivingSourceRoute(location.pathname);
    if (sourceRoute) {
      const sourceChild = match.siblings.find((s) => s.path === sourceRoute);
      if (sourceChild) activeChild = sourceChild;
    }
    return {
      key: match.rootKey,
      label: match.parentLabel,
      children: match.siblings,
      activeChild,
    };
  }, [flat, location.pathname, location.search]);
}
